import {TextField, TextFieldProps} from "@mui/material";
import React, { useState } from "react";
import "./css/CCTextField.scss";

type CCTextFieldLocalProps = {
    onValidate?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    // In MUI v9, TextFieldProps no longer carries InputProps / inputProps;
    // they moved under `slotProps`. We keep the old shape on this wrapper so
    // existing callers don't have to change, then forward into slotProps below.
    InputProps?: any;
    inputProps?: any;
}

type CCTextFieldProps = CCTextFieldLocalProps & Omit<TextFieldProps, "InputProps" | "inputProps">;

export default function CCTextField({
    fullWidth,
    size,
    margin,
    variant,
    onChange,
    onBlur,
    onValidate,
    InputProps,
    inputProps = {},
    // PULLED OUT SO IT CAN BE MERGED RATHER THAN CLOBBERED. This used to arrive inside `...props`,
    // and the `slotProps={{input, htmlInput}}` below sits AFTER that spread -- so a caller passing
    // its own `slotProps` had it silently replaced with `{input: undefined}`.
    //
    // WHAT THAT COST: every login dialog in the suite. Login2's password field passes its
    // show/hide eye as `slotProps.input.endAdornment`, in all seven tiers, and it has never
    // rendered. The code reads correctly, compiles, type-checks and does nothing -- which is why
    // "the toggle is already there" survived a reading of the source and died on the first
    // screenshot of the real page. Found 2026-10-09 looking at prod for an unrelated reason.
    slotProps,
    children,
    label,
    ...props
}: CCTextFieldProps) {

    const [blurred, setBlurred] = useState(false);

    size = size  ? size : label ? "medium" : "small";
    margin = margin  ? margin : label ? "normal" : "none";

    if (InputProps && InputProps.autoComplete === undefined) InputProps.autoComplete = "off";

    if (InputProps) {
        InputProps.inputProps = inputProps = {
            ...(InputProps.inputProps ?? {}),
            ...(inputProps ?? {}),
        };
    }

    if (inputProps.autoCapitalize === undefined && inputProps.autocapitalize === undefined) inputProps.autoCapitalize = "none";
    if (inputProps.autoComplete === undefined && inputProps.autocomplete === undefined) inputProps.autoComplete = "off";
    if (inputProps.spellCheck === undefined && inputProps.spellcheck === undefined) inputProps.spellCheck = "false";

    return (
        <TextField
            fullWidth={fullWidth ? fullWidth : true}
            size={size}
            margin={margin}
            variant={variant}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (onChange) onChange(e);
                if (onValidate) {
                    const value = e.target.value;
                    const shouldValidate = blurred;
                    if (value.length === 0) setBlurred(false);
                    if (shouldValidate) onValidate(e);
                }
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                if (onBlur) onBlur(e);
                if (onValidate) {
                    const value = e.target.value;
                    const validated = blurred;
                    if (value.length > 0) setBlurred(true);
                    if (!validated) onValidate(e);
                }
            }}
            {...props}
            slotProps={{
                ...slotProps,
                // THE LEGACY SHIM WINS over a caller's own slot, deliberately: `InputProps` is
                // this wrapper's documented shape and a caller that passes both has said the same
                // thing twice. When it is absent -- the ordinary case -- whatever the caller put
                // in `slotProps.input` now survives, which is the whole point.
                input: {...(slotProps?.input as object ?? {}), ...(InputProps ?? {})},
                htmlInput: {...(slotProps?.htmlInput as object ?? {}), ...inputProps},
            }}>
            {children}
        </TextField>
    );
}
